/**
 * RoleLifecycleDialogs (S3.4) — safe role lifecycle: deactivate / reactivate /
 * delete behind the governance handler's pre-check seam, so NO member is ever
 * orphaned at zero access (fleet `RoleStatusDialog` / `DeleteRoleDialog`
 * parity, wired to the real two-axis resolver's session catalogs).
 *
 *   • Deactivate / delete first load
 *     `GET /governance/roles/:id/lifecycle-check` — the exclusive members
 *     (users whose ONLY access-contributing role is this one) and the
 *     sanctioned transfer targets.
 *   • When exclusive members exist the transfer Select is REQUIRED (the
 *     handler 409s without it — blocked until transfer). When none exist the
 *     selector is HIDDEN entirely, per the fleet rule: the backend moves ONLY
 *     exclusive holders, so offering a target with zero users at risk would be
 *     a lying affordance.
 *   • Delete is confirm-text gated: the operator types the role's exact name
 *     (a reversible deactivate needs only the reason gate).
 *   • The `isSystem` admin role is locked — the mounting surface must not
 *     offer lifecycle actions on it at all (hide-don't-disable); this dialog
 *     double-guards with an explanatory Alert and no submit footer.
 *   • `InactiveRoleBanner` is the shared banner a role surface mounts when the
 *     shown role is inactive — carrying the reactivate AFFORDANCE, not a bare
 *     status echo (affordance-not-status).
 *
 * API wrappers are slice-local: `systemApi.ts` belongs to another slice's
 * owned set, so the three lifecycle calls live here (recorded follow-up to
 * fold them into `systemApi` once the file frees up). The response shape
 * mirrors `computeRoleLifecycleCheck` in `mocks/handlers/governance.ts` —
 * keep the two in lock-step.
 */

import { useEffect, useMemo } from 'react';
import { Alert, App, Button, Input, Modal, Select, Spin, Tag, Typography, theme } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { clearCache, useRequest } from 'ahooks';
import { AxiosError } from 'axios';
import apiClient, { getUserProfile } from '@/services/api/axios.config';
import type { User } from '@/models/user';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';
import type { GovernanceRole } from './model';

const { Text } = Typography;

const BASE = '/governance';
const ROLES_KEY = '/api/governance/roles';
const USERS_KEY = '/api/governance/users';
const AUDIT_KEY = '/api/governance/audit-logs';

export type RoleLifecycleAction = 'deactivate' | 'reactivate' | 'delete';

/** Mirrors the handler's `RoleLifecycleMember` — keep in lock-step. */
export interface RoleLifecycleMember {
  id: string;
  name: string;
  email: string;
}

/** Mirrors the handler's `computeRoleLifecycleCheck` payload — keep in lock-step. */
export interface RoleLifecycleCheck {
  roleId: string;
  roleName: string;
  isSystem: boolean;
  status: string;
  totalMembers: number;
  exclusiveMembers: RoleLifecycleMember[];
  requireTransfer: boolean;
  availableTargets: { id: string; name: string }[];
}

export interface RoleLifecycleDialogsProps {
  /** The role under change; `null` closes the dialog. */
  role: GovernanceRole | null;
  /** Which lifecycle dialog to show; `null` closes the dialog. */
  action: RoleLifecycleAction | null;
  onClose: () => void;
  /** Fired after a successful lifecycle write so the caller re-reads its lists. */
  onChanged: () => void;
}

interface RoleLifecycleFormData {
  reason: string;
  transferToRoleId?: string;
  confirmText?: string;
}

const EMPTY_FORM: RoleLifecycleFormData = {
  reason: '',
  transferToRoleId: undefined,
  confirmText: '',
};

const actorName = (): string | undefined => (getUserProfile() as User | null)?.name ?? undefined;

/** Map a mock 4xx error code to its localized message key. */
function errorKey(error: unknown): string {
  const code =
    error instanceof AxiosError ? (error.response?.data as { error?: string })?.error : undefined;
  if (code === 'SYSTEM_ROLE_LOCKED') return 'lifecycle.systemLocked';
  if (code === 'EXCLUSIVE_MEMBERS') return 'lifecycle.blockedExclusive';
  if (code === 'CONFIRM_TEXT_MISMATCH') return 'lifecycle.confirmTextMismatch';
  if (code === 'INVALID_TRANSFER_TARGET') return 'lifecycle.invalidTarget';
  return 'messages.loadFailed';
}

/**
 * Shared inactive-role banner (S3.4). Mount on any surface that shows a role;
 * renders nothing while the role is active. Carries the reactivate affordance
 * so the banner is actionable, never a bare status echo.
 */
export interface InactiveRoleBannerProps {
  role: GovernanceRole;
  /** Opens the reactivate dialog; omit on read-only surfaces. */
  onReactivate?: () => void;
}

export const InactiveRoleBanner = ({ role, onReactivate }: InactiveRoleBannerProps) => {
  const { t } = useTranslation('governance');
  if (role.status !== 'inactive') return null;
  return (
    <Alert
      type="warning"
      showIcon
      message={t('lifecycle.inactiveBanner', { name: role.name })}
      action={
        onReactivate ? (
          <Button size="small" onClick={onReactivate}>
            {t('actions.activate')}
          </Button>
        ) : undefined
      }
    />
  );
};

const RoleLifecycleDialogs = ({ role, action, onClose, onChanged }: RoleLifecycleDialogsProps) => {
  const { t } = useTranslation('governance');
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const { message } = App.useApp();

  const open = Boolean(role && action);
  const systemLocked = Boolean(role?.isSystem);
  const roleId = role?.id;
  const roleName = role?.name ?? '';
  /** Only deactivate/delete need the orphan pre-check; reactivate is additive. */
  const needsCheck = Boolean(open && !systemLocked && (action === 'deactivate' || action === 'delete'));

  const {
    data: check,
    loading: checkLoading,
    error: checkError,
  } = useRequest(
    async () => {
      if (!roleId) return undefined;
      const { data } = await apiClient.get<RoleLifecycleCheck>(
        `${BASE}/roles/${roleId}/lifecycle-check`
      );
      return data;
    },
    // `open` in the deps forces a FRESH pre-check on every reopen — membership
    // may have changed since the last look.
    { ready: needsCheck, refreshDeps: [roleId, action, open] }
  );

  const exclusiveMembers = useMemo(() => check?.exclusiveMembers ?? [], [check]);
  const needsTransfer = needsCheck && exclusiveMembers.length > 0;

  // Static schema; the check-dependent rules (transfer target, confirm text)
  // are enforced in onSubmit via setError so a mid-flight pre-check can never
  // race a stale resolver.
  const schema = useMemo(
    () =>
      z.object({
        reason: z.string().trim().min(1, t('reasonModal.reasonRequired')),
        transferToRoleId: z.string().optional(),
        confirmText: z.string().optional(),
      }),
    [t]
  );

  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors, touchedFields, isSubmitting, isSubmitted },
  } = useForm<RoleLifecycleFormData>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY_FORM,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const dialogKey = `${roleId ?? ''}:${action ?? ''}`;
  useEffect(() => {
    if (open) reset(EMPTY_FORM);
  }, [open, dialogKey, reset]);

  const showReasonError = Boolean(errors.reason && (touchedFields.reason || isSubmitted));
  const showTransferError = Boolean(
    errors.transferToRoleId && (touchedFields.transferToRoleId || isSubmitted)
  );
  const showConfirmError = Boolean(errors.confirmText && (touchedFields.confirmText || isSubmitted));

  const onSubmit = handleSubmit(async (data) => {
    if (!role || !action || systemLocked) return;
    if (needsCheck && !check) return; // pre-check still loading / failed
    if (needsTransfer && !data.transferToRoleId) {
      setError('transferToRoleId', { type: 'required', message: t('lifecycle.transferRequired') });
      return;
    }
    if (action === 'delete' && (data.confirmText ?? '').trim() !== role.name) {
      setError('confirmText', { type: 'validate', message: t('lifecycle.confirmTextMismatch') });
      return;
    }
    try {
      if (action === 'delete') {
        await apiClient.delete(`${BASE}/roles/${role.id}`, {
          data: {
            confirmText: (data.confirmText ?? '').trim(),
            transferToRoleId: data.transferToRoleId,
            reason: data.reason.trim(),
            actorName: actorName(),
          },
        });
        message.success(t('lifecycle.deleted', { name: role.name }));
      } else {
        await apiClient.patch(`${BASE}/roles/${role.id}/status`, {
          status: action === 'deactivate' ? 'inactive' : 'active',
          transferToRoleId: data.transferToRoleId,
          reason: data.reason.trim(),
          actorName: actorName(),
        });
        if (action === 'deactivate') {
          message.success(t('lifecycle.deactivated', { name: role.name }));
        } else {
          message.success(t('lifecycle.reactivated', { name: role.name }));
        }
      }
      // Lifecycle writes reproject users + append audit rows server-side —
      // invalidate all three lenses so every tab re-reads.
      clearCache(ROLES_KEY);
      clearCache(USERS_KEY);
      clearCache(AUDIT_KEY);
      onChanged();
      onClose();
    } catch (error) {
      message.error(t(errorKey(error)));
    }
  });

  let title = t('lifecycle.deactivateTitle', { name: roleName });
  let okText = t('actions.deactivate');
  if (action === 'reactivate') {
    title = t('lifecycle.reactivateTitle', { name: roleName });
    okText = t('actions.activate');
  } else if (action === 'delete') {
    title = t('lifecycle.deleteTitle', { name: roleName });
    okText = t('lifecycle.deleteSubmit');
  }

  const buttonHeight = { height: `${controlHeightTokens.controlHeightXL}px` };

  return (
    <Modal
      open={open}
      title={title}
      okText={okText}
      cancelText={t('actions.cancel')}
      onCancel={onClose}
      onOk={onSubmit}
      confirmLoading={isSubmitting}
      okButtonProps={{ danger: action !== 'reactivate', style: buttonHeight }}
      cancelButtonProps={{ style: buttonHeight }}
      maskClosable={false}
      destroyOnHidden
      {...(systemLocked ? { footer: null } : {})}
    >
      {systemLocked ? (
        <Alert type="info" showIcon message={t('lifecycle.systemLocked')} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: token.marginSM }}>
          {action === 'delete' && (
            <Alert type="warning" showIcon message={t('lifecycle.deleteHint')} />
          )}
          {action === 'deactivate' && <Text type="secondary">{t('lifecycle.deactivateHint')}</Text>}
          {action === 'reactivate' && <Text type="secondary">{t('lifecycle.reactivateHint')}</Text>}

          {needsCheck && checkLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: token.paddingLG }}>
              <Spin />
            </div>
          )}
          {needsCheck && !checkLoading && Boolean(checkError) && (
            <Alert type="error" showIcon message={t('messages.loadFailed')} />
          )}

          {needsCheck && check && !checkLoading && (
            <>
              <Text>{t('lifecycle.memberTotal', { count: check.totalMembers })}</Text>
              {exclusiveMembers.length > 0 ? (
                <Alert
                  type="warning"
                  showIcon
                  message={t('lifecycle.exclusiveCount', { count: exclusiveMembers.length })}
                  description={
                    <div
                      style={{ display: 'flex', flexDirection: 'column', gap: token.marginXS }}
                    >
                      <div>
                        <Text style={formStyles.labelStyle}>{t('lifecycle.exclusiveList')}</Text>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: token.marginXXS }}>
                          {exclusiveMembers.map((member) => (
                            <Tag key={member.id}>{member.name}</Tag>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Text style={formStyles.labelStyle}>{t('lifecycle.transferLabel')}</Text>
                        <Controller
                          name="transferToRoleId"
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              size="large"
                              style={{ width: '100%' }}
                              placeholder={t('lifecycle.transferPlaceholder')}
                              aria-label={t('lifecycle.transferLabel')}
                              status={showTransferError ? 'error' : undefined}
                              options={check.availableTargets.map((target) => ({
                                label: target.name,
                                value: target.id,
                              }))}
                              onChange={(value) => {
                                field.onChange(value);
                                clearErrors('transferToRoleId');
                              }}
                            />
                          )}
                        />
                        {showTransferError && (
                          <Text style={formStyles.errorStyle}>
                            {errors.transferToRoleId?.message ?? ''}
                          </Text>
                        )}
                        <Text
                          type="secondary"
                          style={{ display: 'block', marginTop: token.marginXXS }}
                        >
                          {t('lifecycle.transferHint')}
                        </Text>
                      </div>
                    </div>
                  }
                />
              ) : (
                <Text type="secondary">{t('lifecycle.noExclusive')}</Text>
              )}
            </>
          )}

          {action === 'delete' && (
            <div>
              <Text style={formStyles.labelStyle}>
                {t('lifecycle.confirmTextLabel', { name: roleName })}
              </Text>
              <Controller
                name="confirmText"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="large"
                    style={formStyles.inputStyle}
                    placeholder={roleName}
                    aria-label={t('lifecycle.confirmTextLabel', { name: roleName })}
                    status={showConfirmError ? 'error' : undefined}
                  />
                )}
              />
              {showConfirmError && (
                <Text style={formStyles.errorStyle}>{errors.confirmText?.message ?? ''}</Text>
              )}
            </div>
          )}

          <div>
            <Text style={formStyles.labelStyle}>{t('reasonModal.reasonLabel')}</Text>
            <Controller
              name="reason"
              control={control}
              render={({ field }) => (
                <Input.TextArea
                  {...field}
                  rows={3}
                  style={formStyles.inputStyle}
                  placeholder={t('reasonModal.reasonPlaceholder')}
                  aria-label={t('reasonModal.reasonLabel')}
                  status={showReasonError ? 'error' : undefined}
                />
              )}
            />
            {showReasonError && (
              <Text style={formStyles.errorStyle}>{errors.reason?.message ?? ''}</Text>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default RoleLifecycleDialogs;
